import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Edit, Trash2, Search, X, Loader2, Server, HardDrive, Database, Layers, Cloud, Sheet, FileText, Globe, Cpu, Mail } from "lucide-react";
import { useCreateDataSource, useUpdateDataSource } from "@/hooks/useAIInventory";
import { ToolMultiSelect, TECHNOLOGY_OPTIONS, getDataSourceTypeLabel } from "./shared";
import { toast } from "sonner";
import ImportFromProjectsDialog, { Candidate } from "./ImportFromProjectsDialog";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface DataSourcesTabProps {
  dataSources: any[];
  enterpriseSw: any[];
  businessAreas: any[];
  aiToolCatalog: { name: string; vendor: string; category: string }[];
  orgId: string | null;
  isSuperAdmin: boolean;
  selectedOrg: string;
  profileOrgId?: string;
  onDeleteConfirm: (item: { type: string; id: string; name: string }) => void;
}

const iconMap: Record<string, any> = {
  database: Server, file: HardDrive, api: Layers,
  data_lake: Database, data_warehouse: Database, streaming: Layers,
  cloud_storage: Cloud, spreadsheet: Sheet, unstructured: FileText,
  social_media: Globe, iot: Cpu, saas: Cloud, email: Mail,
};

function DataSourceIcon({ type }: { type: string }) {
  const Icon = iconMap[type] || Database;
  return <Icon className="h-4 w-4" />;
}

const DATA_SOURCE_TYPE_OPTIONS = [
  { value: "database", label: "Base de Datos" },
  { value: "file", label: "Archivos" },
  { value: "api", label: "API" },
  { value: "data_lake", label: "Data Lake" },
  { value: "data_warehouse", label: "Data Warehouse" },
  { value: "streaming", label: "Streaming" },
  { value: "cloud_storage", label: "Cloud Storage" },
  { value: "spreadsheet", label: "Hoja de Cálculo" },
  { value: "unstructured", label: "No Estructurado" },
  { value: "social_media", label: "Redes Sociales" },
  { value: "iot", label: "IoT / Sensores" },
  { value: "saas", label: "SaaS / App" },
  { value: "email", label: "Email / Mensajería" },
];

function DataSourceTypeSelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger><SelectValue /></SelectTrigger>
      <SelectContent>
        {DATA_SOURCE_TYPE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

interface DataSourceFormFieldsProps {
  data: any;
  setData: (d: any) => void;
  businessAreas: any[];
  enterpriseSw: any[];
  aiToolCatalog: { name: string; vendor: string; category: string }[];
}

function DataSourceFormFields({ data, setData, businessAreas, enterpriseSw, aiToolCatalog }: DataSourceFormFieldsProps) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2"><Label>Nombre</Label><Input value={data.name} onChange={(e) => setData({...data, name: e.target.value})} placeholder="Ej: ERP Principal" /></div>
        <div className="space-y-2"><Label>Tipo</Label><DataSourceTypeSelect value={data.type} onChange={(v) => setData({...data, type: v})} /></div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Tecnología</Label>
          <Select value={data.technology || undefined} onValueChange={(v) => setData({...data, technology: v})}>
            <SelectTrigger><SelectValue placeholder="Seleccionar tecnología" /></SelectTrigger>
            <SelectContent>{TECHNOLOGY_OPTIONS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Departamento</Label>
          <Select value={data.department || undefined} onValueChange={(v) => setData({...data, department: v})}>
            <SelectTrigger><SelectValue placeholder="Seleccionar departamento" /></SelectTrigger>
            <SelectContent>{businessAreas.map((a: any) => <SelectItem key={a.name} value={a.name}>{a.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2"><Label>Responsable</Label><Input value={data.owner || ''} onChange={(e) => setData({...data, owner: e.target.value})} placeholder="Nombre del responsable" /></div>
        <div className="space-y-2">
          <Label>Software Vinculado</Label>
          <Select value={data.linked_software_id || 'none'} onValueChange={(v) => setData({...data, linked_software_id: v === 'none' ? '' : v})}>
            <SelectTrigger><SelectValue placeholder="Seleccionar software" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Ninguno</SelectItem>
              {enterpriseSw.map((sw: any) => <SelectItem key={sw.id} value={sw.id}>{sw.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-2"><Label>Descripción</Label><Textarea value={data.description || ''} onChange={(e) => setData({...data, description: e.target.value})} placeholder="Breve descripción" rows={2} /></div>
      <div className="space-y-2">
        <Label>Herramientas Conectadas</Label>
        <ToolMultiSelect selected={data.connected_tools || []} onChange={(t) => setData({...data, connected_tools: t})} catalogTools={aiToolCatalog} />
      </div>
    </div>
  );
}

export default function DataSourcesTab({ dataSources, enterpriseSw, businessAreas, aiToolCatalog, orgId, isSuperAdmin, selectedOrg, profileOrgId, onDeleteConfirm }: DataSourcesTabProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);

  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [deptFilter, setDeptFilter] = useState("all");

  const [form, setForm] = useState({ name: "", type: "database", technology: "", department: "", owner: "", description: "", linked_software_id: "", connected_tools: [] as string[] });

  const createDS = useCreateDataSource();
  const qc = useQueryClient();
  const updateDS = useUpdateDataSource();

  const types = useMemo(() => [...new Set(dataSources.map((d: any) => d.type).filter(Boolean))], [dataSources]);
  const depts = useMemo(() => [...new Set(dataSources.map((d: any) => d.department).filter(Boolean))], [dataSources]);

  const filtered = useMemo(() => {
    return dataSources.filter((source: any) => {
      const matchesSearch = (source.name || '').toLowerCase().includes(search.toLowerCase()) ||
                           (source.technology || '').toLowerCase().includes(search.toLowerCase()) ||
                           (source.owner || '').toLowerCase().includes(search.toLowerCase());
      const matchesType = typeFilter === "all" || source.type === typeFilter;
      const matchesDept = deptFilter === "all" || source.department === deptFilter;
      return matchesSearch && matchesType && matchesDept;
    });
  }, [dataSources, search, typeFilter, deptFilter]);

  // Footer summary by type
  const typeCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    dataSources.forEach((ds: any) => { counts[ds.type] = (counts[ds.type] || 0) + 1; });
    return counts;
  }, [dataSources]);

  const getOrgIdForInsert = () => {
    if (isSuperAdmin) {
      if (selectedOrg === 'all') { toast.error('Selecciona una organización específica'); return null; }
      return selectedOrg;
    }
    return profileOrgId;
  };

  const handleAdd = () => {
    const oid = getOrgIdForInsert();
    if (!oid) return;
    const { linked_software_id, ...rest } = form;
    createDS.mutate({ organization_id: oid, ...rest, linked_software_id: linked_software_id || null }, {
      onSuccess: () => {
        setForm({ name: "", type: "database", technology: "", department: "", owner: "", description: "", linked_software_id: "", connected_tools: [] });
        setIsDialogOpen(false);
      }
    });
  };

  const handleEdit = () => {
    if (!editing) return;
    updateDS.mutate({
      id: editing.id, name: editing.name, type: editing.type, technology: editing.technology,
      department: editing.department, owner: editing.owner, description: editing.description,
      connected_tools: editing.connected_tools || [], linked_software_id: editing.linked_software_id || null,
    }, { onSuccess: () => setEditing(null) });
  };

  return (
    <>
      <Card>
        <CardHeader className="flex flex-col gap-4">
          <div className="flex flex-row items-center justify-between">
            <CardTitle>Fuentes de Datos</CardTitle>
            <div className="flex gap-2">
              <ImportFromProjectsDialog
                type="data_sources"
                orgId={orgId}
                existingNames={dataSources.map((d: any) => d.name).filter(Boolean)}
                onImport={async (items: Candidate[]) => {
                  const oid = getOrgIdForInsert();
                  if (!oid) throw new Error("Selecciona organización");
                  const rows = items.map((c) => ({
                    organization_id: oid,
                    name: c.name,
                    type: "database",
                    description:
                      c.sourceProjects.length > 0
                        ? `Importado desde proyectos (${c.sourceProjects.length})`
                        : null,
                    source_projects: c.sourceProjects.length > 0 ? c.sourceProjects : null,
                  }));
                  const { error } = await supabase.from("software_data_sources").insert(rows as any);
                  if (error) throw error;
                  qc.invalidateQueries({ queryKey: ["software_data_sources"] });
                }}
              />
              <Button onClick={() => setIsDialogOpen(true)}><Plus className="mr-2 h-4 w-4" />Agregar Fuente</Button>
            </div>
          </div>

          <div className="flex flex-wrap gap-3 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Buscar fuente de datos..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
              {search && <Button variant="ghost" size="icon" className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6" onClick={() => setSearch("")}><X className="h-3 w-3" /></Button>}
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[150px]"><SelectValue placeholder="Tipo" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los tipos</SelectItem>
                {types.map((t: string) => <SelectItem key={t} value={t}>{getDataSourceTypeLabel(t)}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={deptFilter} onValueChange={setDeptFilter}>
              <SelectTrigger className="w-[150px]"><SelectValue placeholder="Departamento" /></SelectTrigger>
              <SelectContent><SelectItem value="all">Todos</SelectItem>{depts.map((d: string) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
            </Select>
            {(search || typeFilter !== "all" || deptFilter !== "all") && (
              <Button variant="ghost" size="sm" onClick={() => { setSearch(""); setTypeFilter("all"); setDeptFilter("all"); }}>Limpiar filtros</Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Fuente</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Tecnología</TableHead>
                <TableHead>Departamento</TableHead>
                <TableHead>Software</TableHead>
                <TableHead>Responsable</TableHead>
                <TableHead>Herramientas Conectadas</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">{dataSources.length === 0 ? 'No hay fuentes de datos registradas' : 'No se encontraron fuentes con los filtros aplicados'}</TableCell></TableRow>
              ) : filtered.map((source: any) => {
                const linkedSw = enterpriseSw.find((sw: any) => sw.id === source.linked_software_id);
                return (
                  <TableRow key={source.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <DataSourceIcon type={source.type} />
                        <div><div className="font-medium">{source.name}</div><div className="text-xs text-muted-foreground">{source.description}</div></div>
                      </div>
                    </TableCell>
                    <TableCell><Badge variant="outline">{getDataSourceTypeLabel(source.type)}</Badge></TableCell>
                    <TableCell className="text-sm">{source.technology || '—'}</TableCell>
                    <TableCell className="text-sm">{source.department || '—'}</TableCell>
                    <TableCell className="text-sm">{linkedSw ? linkedSw.name : '—'}</TableCell>
                    <TableCell className="text-sm">{source.owner}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(source.connected_tools || []).map((t: string, i: number) => <Badge key={i} variant="secondary" className="text-xs">{t}</Badge>)}
                        {(source.connected_tools || []).length === 0 && <span className="text-muted-foreground text-sm">—</span>}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="icon" onClick={() => setEditing({ ...source })}><Edit className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => onDeleteConfirm({ type: 'datasource', id: source.id, name: source.name })}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <div className="mt-4 pt-4 border-t flex items-center justify-between text-sm text-muted-foreground">
            <span>{filtered.length} de {dataSources.length} fuentes</span>
            <div className="flex flex-wrap items-center gap-3">
              {Object.entries(typeCounts).map(([type, count]) => (
                <span key={type}>{getDataSourceTypeLabel(type)}: <strong className="text-foreground">{count}</strong></span>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      {/* Add Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nueva Fuente de Datos</DialogTitle></DialogHeader>
          <DataSourceFormFields data={form} setData={setForm} businessAreas={businessAreas} enterpriseSw={enterpriseSw} aiToolCatalog={aiToolCatalog} />
          <Button onClick={handleAdd} className="w-full mt-4" disabled={!form.name || createDS.isPending}>
            {createDS.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Agregar Fuente
          </Button>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Editar Fuente de Datos</DialogTitle></DialogHeader>
          {editing && (
            <>
              <DataSourceFormFields data={editing} setData={setEditing} businessAreas={businessAreas} enterpriseSw={enterpriseSw} aiToolCatalog={aiToolCatalog} />
              <Button onClick={handleEdit} className="w-full mt-4" disabled={updateDS.isPending}>
                {updateDS.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Guardar Cambios
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
